import React, { useState } from 'react'
import {
  Alert,
  Box,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  FormControl,
  IconButton,
  InputLabel,
  MenuItem,
  Pagination,
  Select,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Typography,
} from '@mui/material'
import { Cancel as CancelIcon, Save as SaveIcon } from '@mui/icons-material'
import {
  useGetAdminSubscriptionsQuery,
  useUpdateAdminSubscriptionMutation,
  useCancelAdminSubscriptionMutation,
} from '@store/api/adminApi'
import AdminPageGate from '@components/auth/AdminPageGate'

const TIERS = ['free', 'premium', 'pro'] as const

const tierColor = (tier: string): 'default' | 'primary' | 'secondary' => {
  if (tier === 'pro') return 'secondary'
  if (tier === 'premium') return 'primary'
  return 'default'
}

const PAGE_SIZE = 20

const SubscriptionManagement: React.FC = () => {
  const [page, setPage] = useState(1)
  const [tierFilter, setTierFilter] = useState('')
  const [search, setSearch] = useState('')
  const [pendingTier, setPendingTier] = useState<Record<string, string>>({})
  const [message, setMessage] = useState<{ type: 'success' | 'error'; text: string } | null>(null)

  const { data, isLoading, refetch } = useGetAdminSubscriptionsQuery({
    tier: tierFilter || undefined,
    search: search.trim() || undefined,
    limit: PAGE_SIZE,
    page,
  })
  const [updateSubscription, { isLoading: isUpdating }] = useUpdateAdminSubscriptionMutation()
  const [cancelSubscription, { isLoading: isCancelling }] = useCancelAdminSubscriptionMutation()

  const users = data?.users ?? []
  const pagination = data?.pagination
  const totalUsers = pagination?.total ?? users.length

  const handlePageChange = (_: React.ChangeEvent<unknown>, value: number) => {
    setPage(value)
  }

  const showError = (err: unknown, fallback: string) => {
    const apiErr = err as { data?: { message?: string } }
    setMessage({ type: 'error', text: apiErr?.data?.message ?? fallback })
  }

  const handleSaveTier = async (userId: string) => {
    const tier = pendingTier[userId]
    if (!tier) return
    setMessage(null)
    try {
      await updateSubscription({ id: userId, subscriptionTier: tier }).unwrap()
      setPendingTier((prev) => {
        const next = { ...prev }
        delete next[userId]
        return next
      })
      setMessage({ type: 'success', text: 'Subscription tier updated' })
      refetch()
    } catch (err: unknown) {
      showError(err, 'Failed to update subscription')
    }
  }

  const handleCancel = async (userId: string) => {
    setMessage(null)
    try {
      await cancelSubscription(userId).unwrap()
      setMessage({ type: 'success', text: 'User moved to free tier' })
      refetch()
    } catch (err: unknown) {
      showError(err, 'Failed to cancel subscription')
    }
  }

  return (
    <AdminPageGate permission="subscriptions:read" title="Subscriptions">
    <Box>
      <Typography variant="h4" component="h1" gutterBottom fontWeight={600}>
        Subscriptions
      </Typography>
      <Typography variant="body2" color="text.secondary" paragraph>
        View and adjust user plan tiers. Paid billing and refunds are not integrated yet.
      </Typography>

      {message && (
        <Alert severity={message.type} sx={{ mb: 2 }} onClose={() => setMessage(null)}>
          {message.text}
        </Alert>
      )}

      <Card sx={{ mb: 3 }}>
        <CardContent>
          <Stack direction={{ xs: 'column', sm: 'row' }} spacing={2}>
            <TextField
              label="Search email or username"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value)
                setPage(1)
              }}
              size="small"
              fullWidth
            />
            <FormControl size="small" sx={{ minWidth: 160 }}>
              <InputLabel id="tier-filter-label">Tier</InputLabel>
              <Select
                labelId="tier-filter-label"
                label="Tier"
                value={tierFilter}
                onChange={(e) => {
                  setTierFilter(e.target.value)
                  setPage(1)
                }}
              >
                <MenuItem value="">All tiers</MenuItem>
                {TIERS.map((t) => (
                  <MenuItem key={t} value={t}>
                    {t}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Stack>
        </CardContent>
      </Card>

      <Card>
        <CardContent>
          <Typography variant="h6" gutterBottom>
            Users ({totalUsers})
          </Typography>
          {isLoading ? (
            <CircularProgress size={28} />
          ) : users.length === 0 ? (
            <Typography variant="body2" color="text.secondary">
              No users match this filter.
            </Typography>
          ) : (
            <TableContainer>
              <Table size="small">
                <TableHead>
                  <TableRow>
                    <TableCell>User</TableCell>
                    <TableCell>Current tier</TableCell>
                    <TableCell>Change to</TableCell>
                    <TableCell align="right">Actions</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {users.map((u) => {
                    const draft = pendingTier[u.id] ?? u.subscriptionTier
                    const dirty = draft !== u.subscriptionTier
                    return (
                      <TableRow key={u.id}>
                        <TableCell>
                          <Typography variant="body2">{u.email}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            @{u.username}
                          </Typography>
                        </TableCell>
                        <TableCell>
                          <Chip label={u.subscriptionTier} size="small" color={tierColor(u.subscriptionTier)} />
                        </TableCell>
                        <TableCell>
                          <FormControl size="small" fullWidth>
                            <Select
                              value={draft}
                              onChange={(e) =>
                                setPendingTier((prev) => ({ ...prev, [u.id]: e.target.value }))
                              }
                              aria-label={`Tier for ${u.email}`}
                            >
                              {TIERS.map((t) => (
                                <MenuItem key={t} value={t}>
                                  {t}
                                </MenuItem>
                              ))}
                            </Select>
                          </FormControl>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title={dirty ? 'Save tier' : 'No changes'}>
                            <span>
                              <IconButton
                                size="small"
                                color="primary"
                                disabled={!dirty || isUpdating}
                                onClick={() => handleSaveTier(u.id)}
                                aria-label={`Save tier for ${u.email}`}
                              >
                                <SaveIcon fontSize="small" />
                              </IconButton>
                            </span>
                          </Tooltip>
                          {u.subscriptionTier !== 'free' && (
                            <Tooltip title="Cancel (set free)">
                              <IconButton
                                size="small"
                                color="warning"
                                disabled={isCancelling}
                                onClick={() => handleCancel(u.id)}
                                aria-label={`Cancel subscription for ${u.email}`}
                              >
                                <CancelIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </TableCell>
                      </TableRow>
                    )
                  })}
                </TableBody>
              </Table>
            </TableContainer>
          )}
          {pagination && pagination.totalPages > 1 && (
            <Box display="flex" justifyContent="center" mt={3}>
              <Pagination
                count={pagination.totalPages}
                page={page}
                onChange={handlePageChange}
                color="primary"
              />
            </Box>
          )}
        </CardContent>
      </Card>
    </Box>
    </AdminPageGate>
  )
}

export default SubscriptionManagement
